import { useEffect, useRef, useState } from 'react';
import type { KioskServerMsg, PendingPurchase } from '@csoki/shared';

type State =
  | { kind: 'connecting' }
  | { kind: 'idle' }
  | { kind: 'pending'; current: PendingPurchase }
  | { kind: 'error'; message: string };

export function useKioskWs(kioskId: string): State {
  const [state, setState] = useState<State>({ kind: 'connecting' });
  const retryRef = useRef(0);

  useEffect(() => {
    let closed = false;
    let ws: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      if (closed) return;
      const proto = location.protocol === 'https:' ? 'wss' : 'ws';
      ws = new WebSocket(`${proto}://${location.host}/api/kiosk/${kioskId}/ws`);
      ws.onmessage = (ev) => {
        const msg = JSON.parse(ev.data as string) as KioskServerMsg;
        retryRef.current = 0;
        if (msg.type === 'state') {
          if (msg.current === null) setState({ kind: 'idle' });
          else setState({ kind: 'pending', current: msg.current });
        } else {
          setState({ kind: 'error', message: msg.message });
        }
      };
      ws.onclose = () => {
        if (closed) return;
        const delay = Math.min(10_000, 500 * Math.pow(2, retryRef.current++));
        setState((s) => (s.kind === 'pending' ? s : { kind: 'connecting' }));
        reconnectTimer = setTimeout(connect, delay);
      };
      ws.onerror = () => {
        ws?.close();
      };
    }
    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      ws?.close();
    };
  }, [kioskId]);

  return state;
}
